import { Flag, Variant } from '@acies/domain';
import { IFlagJSON } from '@acies/schema';
import { HTMLSelectListEvent } from '@acies/ui';
import { FeatureFlagsAdminModel } from '../model/model';
import { FlagsService } from '../services/flags.service';

/** The Flags Controller is responsible for loading all feature flags on startup and mutating the flag.enabled property. */
export class FlagsController {
	#flagsService: FlagsService;
	#model: FeatureFlagsAdminModel;
	public constructor(flagsService: FlagsService, model: FeatureFlagsAdminModel) {
		this.#flagsService = flagsService;
		this.#model = model;
	}

	public loadFlags() {
		return this.#flagsService.loadFlags(this.#model.accountsMap);
	}

	public flagSelectedChanged(event: Event): void {
		if (event instanceof HTMLSelectListEvent) {
			this.#updateSelectedFlag(event);
			this.#updateAddVariantsButtonEnabled();
			this.#model.updateOrganizationVariants();
		}
	}

	#updateSelectedFlag(event: HTMLSelectListEvent): void {
		if (event.selectedItem instanceof Flag) {
			this.#model.selectedFlag = event.selectedItem;
		} else {
			this.#model.selectedFlag = null;
		}
	}

	#updateAddVariantsButtonEnabled(): void {
		this.#model.addVariantsTopLevelButtonEnabled = this.#model.selectedFlag instanceof Flag;
	}

	/** Saves the date from the edit-date dialog as the flag's automatic enablement date; the old date is restored if the save fails. */
	public saveExpiresAt(): void {
		const flag = this.#model.editingFlag;
		if (!flag || !this.#model.editDateSavable) {
			return;
		}
		const [year, month, day] = this.#model.editDateValue.split('-').map(Number);
		// local midnight, matching how the list formats the date
		const expiresAt = new Date(year, month - 1, day);
		const previous = flag.expiresAt;
		flag.expiresAt = expiresAt;
		this.#model.updatingFlags = true;
		this.#flagsService.updateFlags(this.#model.modelAsBodyJSON).subscribe((value) => {
			this.#model.updatingFlags = false;
			this.#model.hideEditDateDialog();
			if (value instanceof Error) {
				flag.expiresAt = previous;
				this.#model.displayErrorDialogFromError(value);
			}
		});
	}

	public hideConfirmationDialog() {
		if (this.#model.updatingInstance) {
			// revert the enabled flag if we cancel in the modal
			this.#model.updatingInstance.enabled = !this.#model.updatingInstance.enabled;
		}
		this.#model.hideConfirmationDialog();
	}

	public confirmationSave() {
		if (this.#model.updatingInstance) {
			this.#updateFlags(this.#model.updatingInstance);
		}
	}

	#updateFlags(instance: Flag | Variant) {
		this.#model.updatingFlags = true;
		// get the model serialized as the IFlagJSON[] that the API accepts
		const body: IFlagJSON[] = this.#model.modelAsBodyJSON;
		this.#flagsService.updateFlags(body).subscribe((value) => {
			this.#model.hideConfirmationDialog();
			this.#model.updatingFlags = false;
			if (value instanceof Error) {
				// if we get an error, revert the enabled property that is bound to the UI check-box component,
				// so we get correct UI state that reflects the model.
				instance.enabled = !instance.enabled;
				this.#model.displayErrorDialogFromError(value);
			}
		});
	}
}
